import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Pagination } from "@mui/material";
import { DarkMode, LightMode, Visibility } from "@mui/icons-material";
import { toast } from "react-toastify";
import axios from "axios";
import { server } from "../server";
import {
  Edit,
  BookOpen,
  Users,
  Calendar,
  Star,
  TrendingUp,
} from "lucide-react";
import SubjectDetails from "../Components/subject/SubjectDetails";
import UpdateSubject from "../Components/subject/UpdateSubject";

export default function SubjectsList() {
  const [currentPage, setCurrentPage] = useState(1);
  const [subjectsData, setSubjectsData] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [editSubject, setEditSubject] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const itemsPerPage = 6;

  const fetchSubjectsList = async () => {
    try {
      setIsLoading(true);
      const { data } = await axios.get(`${server}/subject/all`, {
        params: {
          pageNo: 1,
          pageSize: 100,
        },
      });

      setSubjectsData(data?.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
      setSubjectsData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSubjectsList();
    const savedTheme = localStorage.getItem("darkMode");
    const systemPrefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)"
    ).matches;

    if (savedTheme !== null) {
      setDarkMode(JSON.parse(savedTheme));
    } else {
      setDarkMode(systemPrefersDark);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  // Calculate pagination
  const totalPages = Math.ceil(subjectsData?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentSubjects = subjectsData?.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleViewSubject = (subject) => {
    setSelectedSubject(subject);
    setIsViewModalOpen(true);
  };

  const handleEditSubject = (subject) => {
    setEditSubject({ ...subject });
    setIsEditModalOpen(true);
  };

  const handleCloseModals = () => {
    setIsViewModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedSubject(null);
    setEditSubject(null);
  };

  const handleSaveEdit = (updatedSubject) => {
    if (updatedSubject) {
      setSubjectsData((prev) =>
        prev?.map((subject) =>
          subject.id === updatedSubject.id ? updatedSubject : subject
        )
      );
      handleCloseModals();
      toast.success("Subject updated successfully!");
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  // Theme classes
  const bgGradient = darkMode
    ? "bg-gradient-to-br from-gray-900 to-gray-800"
    : "bg-gradient-to-br from-blue-50 to-indigo-100";

  const cardBg = darkMode ? "bg-gray-800" : "bg-white";
  const cardBorder = darkMode ? "border-gray-700" : "border-gray-200";
  const textPrimary = darkMode ? "text-white" : "text-gray-900";
  const textSecondary = darkMode ? "text-gray-300" : "text-gray-600";
  const textMuted = darkMode ? "text-gray-400" : "text-gray-500";
  const headerBorder = darkMode ? "border-gray-700" : "border-gray-200";
  const footerBg = darkMode ? "bg-gray-900/50" : "bg-gray-50";

  // Loading skeleton
  const LoadingSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={index}
          className={`${cardBg} rounded-2xl shadow-lg border ${cardBorder} p-6 animate-pulse`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gray-400 rounded-lg"></div>
              <div className="h-6 bg-gray-400 rounded w-32"></div>
            </div>
            <div className="h-6 bg-gray-400 rounded w-16"></div>
          </div>
          <div className="h-4 bg-gray-400 rounded w-full mb-2"></div>
          <div className="h-4 bg-gray-400 rounded w-3/4 mb-4"></div>
          <div className="flex flex-wrap gap-2 mb-4">
            <div className="h-6 bg-gray-400 rounded w-16"></div>
            <div className="h-6 bg-gray-400 rounded w-20"></div>
            <div className="h-6 bg-gray-400 rounded w-14"></div>
          </div>
          <div className="flex justify-between items-center">
            <div className="h-4 bg-gray-400 rounded w-20"></div>
            <div className="flex space-x-2">
              <div className="h-8 w-8 bg-gray-400 rounded"></div>
              <div className="h-8 w-8 bg-gray-400 rounded"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div
      className={`min-h-screen ${bgGradient} py-8 px-4 transition-colors duration-300`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header with Dark Mode Toggle */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 relative"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleDarkMode}
            className={`absolute right-4 top-0 p-3 rounded-full ${
              darkMode
                ? "bg-yellow-400 text-gray-900"
                : "bg-gray-800 text-white"
            } transition-colors duration-300 shadow-lg`}
            title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
          >
            {darkMode ? <LightMode /> : <DarkMode />}
          </motion.button>

          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="p-3 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl shadow-lg">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
            <h1 className={`text-4xl font-bold ${textPrimary}`}>
              Available Subjects
            </h1>
          </div>
          <p className={`text-lg ${textSecondary} max-w-2xl mx-auto`}>
            Explore our comprehensive collection of subjects tailored for
            various competitive exams and academic courses
          </p>
        </motion.div>

        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={`grid grid-cols-1 md:grid-cols-4 gap-4 mb-8 p-6 rounded-2xl ${cardBg} border ${cardBorder} shadow-lg`}
        >
          <div className="text-center">
            <div className={`text-2xl font-bold ${textPrimary} mb-1`}>
              {subjectsData?.length || 0}
            </div>
            <div
              className={`text-sm ${textMuted} flex items-center justify-center gap-1`}
            >
              <BookOpen className="w-4 h-4" />
              Total Subjects
            </div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${textPrimary} mb-1`}>
              {subjectsData?.filter((sub) => sub.isPopular)?.length || 0}
            </div>
            <div
              className={`text-sm ${textMuted} flex items-center justify-center gap-1`}
            >
              <Star className="w-4 h-4" />
              Popular Subjects
            </div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${textPrimary} mb-1`}>
              {subjectsData?.reduce(
                (total, sub) => total + (sub.chapters?.length || 0),
                0
              )}
            </div>
            <div
              className={`text-sm ${textMuted} flex items-center justify-center gap-1`}
            >
              <TrendingUp className="w-4 h-4" />
              Total Chapters
            </div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${textPrimary} mb-1`}>
              {subjectsData?.reduce(
                (total, sub) => total + (sub.exams?.length || 0),
                0
              )}
            </div>
            <div
              className={`text-sm ${textMuted} flex items-center justify-center gap-1`}
            >
              <Users className="w-4 h-4" />
              Total Exams
            </div>
          </div>
        </motion.div>

        {/* Subjects Grid */}
        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12"
            >
              <AnimatePresence>
                {currentSubjects?.map((subject) => (
                  <motion.div
                    key={subject.id}
                    variants={cardVariants}
                    whileHover={{ y: -5, scale: 1.02 }}
                    className={`${cardBg} rounded-2xl shadow-lg overflow-hidden border ${cardBorder} transition-all duration-300 group cursor-pointer`}
                  >
                    {/* Card Header */}
                    <div
                      className={`p-6 border-b ${headerBorder} bg-gradient-to-r ${
                        subject.isPopular
                          ? "from-orange-500/10 to-red-500/10"
                          : darkMode
                          ? "from-gray-700 to-gray-800"
                          : "from-blue-50 to-indigo-50"
                      }`}
                      onClick={() => handleViewSubject(subject)}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-lg">
                            <span className="text-2xl">
                              {subject.icon || "📚"}
                            </span>
                          </div>
                          <div>
                            <h3
                              className={`text-xl font-bold ${textPrimary} group-hover:text-blue-500 transition-colors duration-200`}
                            >
                              {subject.subject}
                            </h3>
                            <p className={`text-sm ${textMuted} mt-1`}>
                              ID: {subject.id}
                            </p>
                          </div>
                        </div>
                        {subject.isPopular && (
                          <motion.span
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="px-3 py-1 bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm font-bold rounded-full shadow-lg flex items-center gap-1"
                          >
                            <Star className="w-3 h-3 fill-current" />
                            Popular
                          </motion.span>
                        )}
                      </div>

                      <p
                        className={`text-sm ${textSecondary} line-clamp-2 mb-3`}
                      >
                        {subject.description || "No description available"}
                      </p>

                      <div
                        className={`flex items-center text-sm ${textMuted} gap-4`}
                      >
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(subject.createdAt).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <BookOpen className="w-4 h-4" />
                          {subject.chapters?.length || 0} chapters
                        </span>
                      </div>
                    </div>

                    {/* Exams Section */}
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                      <h4
                        className={`text-sm font-semibold ${textSecondary} mb-3 flex items-center gap-2`}
                      >
                        <Users className="w-4 h-4" />
                        Related Exams ({subject.exams?.length || 0})
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {subject.exams?.slice(0, 3)?.map((exam, index) => (
                          <motion.span
                            key={exam}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: index * 0.1 }}
                            className={`px-3 py-1 ${
                              darkMode
                                ? "bg-blue-900/50 text-blue-200 border border-blue-700/50"
                                : "bg-blue-100 text-blue-700 border border-blue-200"
                            } text-xs font-medium rounded-full transition-all duration-300 hover:scale-105`}
                          >
                            {exam}
                          </motion.span>
                        ))}
                        {subject.exams?.length > 3 && (
                          <span
                            className={`px-3 py-1 ${
                              darkMode
                                ? "bg-gray-700 text-gray-300"
                                : "bg-gray-100 text-gray-600"
                            } text-xs font-medium rounded-full transition-colors duration-300`}
                          >
                            +{subject.exams?.length - 3} more
                          </span>
                        )}
                        {(!subject.exams || subject.exams.length === 0) && (
                          <span className={`text-xs ${textMuted} italic`}>
                            No exams listed
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Chapters Section */}
                    <div className="p-6">
                      <h4
                        className={`text-sm font-semibold ${textSecondary} mb-3 flex items-center gap-2`}
                      >
                        <TrendingUp className="w-4 h-4" />
                        Chapters ({subject.chapters?.length || 0})
                      </h4>
                      <div className="space-y-2">
                        {subject.chapters
                          ?.slice(0, 3)
                          ?.map((chapter, index) => (
                            <motion.div
                              key={chapter.id}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.1 }}
                              className={`flex items-center gap-2 p-2 rounded-lg ${
                                darkMode ? "bg-gray-700/50" : "bg-gray-50"
                              }`}
                            >
                              <div
                                className={`w-2 h-2 rounded-full ${
                                  darkMode ? "bg-green-400" : "bg-green-500"
                                }`}
                              ></div>
                              <span
                                className={`text-sm ${textSecondary} truncate`}
                              >
                                {chapter.chapter}
                              </span>
                            </motion.div>
                          ))}
                        {subject.chapters?.length > 3 && (
                          <div
                            className={`text-center p-2 rounded-lg ${
                              darkMode ? "bg-gray-700/30" : "bg-gray-100"
                            }`}
                          >
                            <span className={`text-xs ${textMuted}`}>
                              +{subject.chapters.length - 3} more chapters
                            </span>
                          </div>
                        )}
                        {(!subject.chapters ||
                          subject.chapters.length === 0) && (
                          <div
                            className={`text-center p-3 rounded-lg ${
                              darkMode ? "bg-gray-700/30" : "bg-gray-100"
                            }`}
                          >
                            <span className={`text-xs ${textMuted} italic`}>
                              No chapters available
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Card Footer */}
                    <div
                      className={`px-6 py-4 ${footerBg} border-t ${headerBorder}`}
                    >
                      <div className="flex justify-between items-center">
                        <span className={`text-sm ${textMuted}`}>
                          Created by: User {subject.createdBy}
                        </span>
                        <div className="flex space-x-2">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewSubject(subject);
                            }}
                            className={`p-2 ${
                              darkMode
                                ? "text-gray-400 hover:text-blue-400 hover:bg-blue-900/50"
                                : "text-gray-600 hover:text-blue-600 hover:bg-blue-50"
                            } rounded-lg transition-all duration-300 flex gap-1 items-center`}
                            title="View Details"
                          >
                            <Visibility fontSize="small" />
                            <span className="text-xs">View</span>
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditSubject(subject);
                            }}
                            className={`p-2 ${
                              darkMode
                                ? "text-gray-400 hover:text-green-400 hover:bg-green-900/50"
                                : "text-gray-600 hover:text-green-600 hover:bg-green-50"
                            } rounded-lg transition-all duration-300 flex gap-1 items-center`}
                            title="Edit Subject"
                          >
                            <Edit className="w-4 h-4" />
                            <span className="text-xs">Edit</span>
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>

            {/* Pagination */}
            {totalPages > 1 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="flex justify-center"
              >
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      fontSize: "1rem",
                      fontWeight: "600",
                      color: darkMode ? "#E5E7EB" : "#374151",
                      "&.Mui-selected": {
                        background: "linear-gradient(135deg, #3B82F6, #6366F1)",
                        color: "white",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #2563EB, #4F46E5)",
                        },
                      },
                      "&:hover": {
                        backgroundColor: darkMode
                          ? "rgba(255, 255, 255, 0.1)"
                          : "rgba(0, 0, 0, 0.04)",
                      },
                    },
                  }}
                />
              </motion.div>
            )}
          </>
        )}

        {/* Modals */}
        <SubjectDetails
          isOpen={isViewModalOpen}
          subject={selectedSubject}
          onClose={handleCloseModals}
          onEdit={() => {
            setIsViewModalOpen(false);
            handleEditSubject(selectedSubject);
          }}
          darkMode={darkMode}
        />

        <UpdateSubject
          isOpen={isEditModalOpen}
          subject={editSubject}
          onClose={handleCloseModals}
          onSave={handleSaveEdit}
          darkMode={darkMode}
        />

        {/* Empty State */}
        {!isLoading && subjectsData?.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <div className="text-8xl mb-6">📚</div>
            <h3 className={`text-3xl font-bold ${textPrimary} mb-4`}>
              No Subjects Available
            </h3>
            <p className={`text-lg ${textSecondary} max-w-md mx-auto mb-6`}>
              It looks like there are no subjects in the system yet. Subjects
              will appear here once they are added.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={fetchSubjectsList}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Refresh Page
            </motion.button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
